import { create } from "zustand"
import { persist } from "zustand/middleware"

export interface MediaPlayerSettings {
  // Playback
  playbackSpeed: number // 0.25 - 3.0
  loop: boolean
  muted: boolean
  preservesPitch: boolean
  audioScrubbing: boolean
  isReversed: boolean

  // Overlay
  showScriptOverlay: boolean
  overlayOpacity: number // 0-100
  overlayStyle: "bar" | "dot" | "line"

  // UI
  videoHeight: string // "30vh", "40vh", "50vh"
  autoHideControls: boolean
}

interface MediaPlayerSettingsStore extends MediaPlayerSettings {
  setPlaybackSpeed: (speed: number) => void
  setLoop: (loop: boolean) => void
  setMuted: (muted: boolean) => void
  setPreservesPitch: (preserve: boolean) => void
  setAudioScrubbing: (enabled: boolean) => void
  setIsReversed: (reversed: boolean) => void
  setShowScriptOverlay: (show: boolean) => void
  setOverlayOpacity: (opacity: number) => void
  setOverlayStyle: (style: "bar" | "dot" | "line") => void
  setVideoHeight: (height: string) => void
  setAutoHideControls: (autoHide: boolean) => void
  resetToDefaults: () => void
}

const defaultSettings: MediaPlayerSettings = {
  playbackSpeed: 1,
  loop: false,
  muted: false,
  preservesPitch: true,
  audioScrubbing: true,
  isReversed: false,
  showScriptOverlay: false,
  overlayOpacity: 70,
  overlayStyle: "bar",
  videoHeight: "40vh",
  autoHideControls: false,
}

export const useMediaPlayerSettingsStore = create<MediaPlayerSettingsStore>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setPlaybackSpeed: (speed) => set({ playbackSpeed: speed }),
      setLoop: (loop) => set({ loop }),
      setMuted: (muted) => set({ muted }),
      setPreservesPitch: (preserve) => set({ preservesPitch: preserve }),
      setAudioScrubbing: (enabled) => set({ audioScrubbing: enabled }),
      setIsReversed: (reversed) => set({ isReversed: reversed }),
      setShowScriptOverlay: (show) => set({ showScriptOverlay: show }),
      setOverlayOpacity: (opacity) => set({ overlayOpacity: opacity }),
      setOverlayStyle: (style) => set({ overlayStyle: style }),
      setVideoHeight: (height) => set({ videoHeight: height }),
      setAutoHideControls: (autoHide) => set({ autoHideControls: autoHide }),
      resetToDefaults: () => set(defaultSettings),
    }),
    {
      name: "media-player-settings",
    }
  )
)
